import React, {Dispatch, ReactElement, SetStateAction, useEffect, useState} from "react";
import {
    CodeIcon,
    DownloadIcon,
    FormInputIcon,
    Maximize2Icon,
    SearchIcon,
    XIcon,
    ColumnsIcon,
    Columns3
} from "lucide-react";
import {cn} from "components/ui/lib/utils";
import axios from "../utility/customAxios";
import {plainToInstance} from "class-transformer";
import {FilterPanel, FilterSelector, TelemetryLevelFilters} from "components/Filter/Filter";
import {InputPill, Pill} from "components/Filter/Pill";
import {Attribute} from "types/telemetry";
import {getLogLeveLIndicatorColor} from "components/SidePanel/Log/utils";
import LogPanelContent from "components/SidePanel/Log/LogDetails";
import {Drawer} from "vaul";
import {SavedLogSearchesDrawer} from "../components/SavedSearches/SavedLogSearchesDrawer";
import {BaseView} from "./BaseView";
import {useSelector} from "react-redux";
import timerange from "../store/reducers/timerange";
import {AxiosPromise} from "axios";
import {MetricToChartData, MetricType} from "./MetricsTest";
import {LoadingSpinner} from "../components/ui/customSpinner";
import {useSearchParams} from "react-router-dom";
import {useDebouncedCallback} from "use-debounce";
import {TimeRange} from "../types/time";
import {DateTimePrecision, displayDateTimeWithSelectedFormat} from "../utility/displayDateTime";
import {
    convertMetoroQLToMetricSpecifier,
    convertMetricSpecifierToMetoroQL,
    Metric,
    MetricSpecifier
} from "../clients/metoro/metrics";
import {ChartType, MetoroChart} from "../components/Charts/MetoroChart";
import {MultiMetoroMetricsChart} from "../components/Charts/MultiMetoroMetricsCharts";
import {Checkbox} from "../components/ui/checkbox";
import {Textarea} from "../components/ui/textarea";
import {toast, useToast} from "../components/ui/use-toast";
import {Tooltip, TooltipContent, TooltipTrigger} from "../components/ui/tooltip";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import {Button} from "../components/ui/button";
import {Input} from "../components/ui/input";
import {Label} from "../components/ui/label";
import {Popover, PopoverContent, PopoverTrigger} from "../components/ui/popover";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,} from "../components/ui/command";
import {TransformAttributeToggle} from "../components/Dashboarding/widgets/TransformAttributeToggle";
import {AddTransformButton, Transform, TransformSelector, TransformTypes} from "../components/Dashboarding/widgets/TransformSelector";

export interface Log {
    // The time that the log line was emitted in milliseconds since the epoch
    time: number,
    // The severity of the log line
    severity: string,
    // The log message
    message: string
    // The attributes of the log line
    logAttributes: Map<string, string>
    // The attributes of the resource that emitted the log line
    resourceAttributes: Map<string, string>
    // Service name
    serviceName: string
}


export interface Logs {
    logs: Log[]
    filterAttributes: Map<string, Attribute[]>
}

const customLogColours = new Map<Record<string, string>, string[]>(
    [[
        {key: "log_level", value: "error",},
        ["#ff6384", "#ff638433"]
    ],
        [
            {key: "log_level", value: "warning",},
            ["#ff9f40", "#ff9f4033"]
        ],
        [
            {key: "log_level", value: "info",},
            ["#36a2eb", "#36a2eb33"]
        ],
        [
            {key: "log_level", value: "unknown",},
            ["#6b7280", "#c9cbcf33"]
        ],

    ])

export type TimelineViewerProps = {
    startTime?: number,
    endTime?: number,
    metric?: Metric,
    metricSpecifier?: MetricSpecifier,
    metoroQLQuery?: string,
    title: string,
    customCategoryColour?: Map<Record<string, string>, string[]>
}

export function TimelineViewer(props: TimelineViewerProps) {
    const [environments, setEnvironments] = useState<string[]>([]);
    const [searchParams, setSearchParams] = useSearchParams()
    useEffect(() => {
        let environments = searchParams.get("environment");
        if (environments !== null && environments !== "") {
            setEnvironments([environments])
        }
        if (environments === "") {
            setEnvironments([])
        }
    }, [searchParams])

    // If we have a MetoroQL query, use it
    if (props.metoroQLQuery) {
        return <div className={"flex-none w-full h-[180px] bg-backgroundmedium mb-4 flex"}>
            <MultiMetoroMetricsChart
                metricSpecifiers={[]}
                hideLegend
                className={"grow shrink flex"}
                customColours={props.customCategoryColour ? props.customCategoryColour : customLogColours}
                metoroQLQueries={[{query: props.metoroQLQuery, bucketSize: 0}]}
                type={ChartType.Bar}
                startTime={props.startTime!}
                endTime={props.endTime!}/>
        </div>
    }

    // If no MetoroQL query and no metric specifier or metric, return null
    if (props.metricSpecifier === undefined && props.metric === undefined) {
        return null;
    }

    // Use metric specifier if available
    if (props.metricSpecifier !== undefined) {
        let filtersToUse = new Map(props.metricSpecifier.filters) ?? new Map<string, string[]>();
        if (filtersToUse.has("environment") && filtersToUse.get("environment")!.length > 0) {
            // Do not override as the chart has set it
        } else {
            if (environments.length > 0 && environments[0] !== "") {
                let envToUse = environments.filter((env) => env !== "")
                filtersToUse.set("environment", envToUse)
            } else {
                filtersToUse.delete("environment")
            }
        }
        const metricSpecifierToUse = {
            ...props.metricSpecifier,
            filters: filtersToUse
        }

        return <div className={"flex-none w-full h-[180px] bg-backgroundmedium mb-4 flex"}>
            <MultiMetoroMetricsChart
                hideLegend
                className={"grow shrink flex"}
                customColours={props.customCategoryColour ? props.customCategoryColour : customLogColours}
                metricSpecifiers={[metricSpecifierToUse]} type={ChartType.Bar} startTime={props.startTime!}
                endTime={props.endTime!}/>
        </div>
    }

    // Use metric if available
    if (props.metric !== undefined) {
        return <div className={"flex-none w-full h-[180px] bg-backgroundmedium mb-4 flex"}>
            <MetoroChart
                hideLegend={true}
                className={"h-full"} title={props.title} type={ChartType.Bar}
                dataToUse={MetricToChartData(props.metric, undefined, undefined, props.customCategoryColour ? props.customCategoryColour : customLogColours)}/>
        </div>
    }
}


function LogLevelFilters(props: {
    filter: Map<string, string[]>,
    attributes: Map<string, Attribute[]>,
    setFilter: (filter: Map<string, string[]>) => void
    excludeFilter: Map<string, string[]>,
    setExcludeFilter: (filter: Map<string, string[]>) => void
}) {
    const levels = props.attributes.get("log_level");
    if (!levels) {
        return null;
    }
    const levelFilters: ReactElement[] = [];
    for (const level of levels) {
        const levelName = level.value;
        const numLogs = level.volume;
        const indicatorColor = getLogLeveLIndicatorColor(levelName);
        levelFilters.push(<FilterSelector filterFilter={""} indicatorColor={indicatorColor} key={levelName}
                                          filterKey={"log_level"}
                                          filterValue={levelName}
                                          volume={numLogs} filter={props.filter} setFilter={props.setFilter}
                                          setExcludeFilter={props.setExcludeFilter}
                                          excludeFilter={props.excludeFilter}
        />)
    }

    return <div className={"py-2"}>
        {levelFilters}
    </div>
}

export function LogSearch(props: {
    embeded?: boolean,
    filter: Map<string, string[]>,
    setFilter: (filter: Map<string, string[]>) => void,
    setFreeTextFilter: (filter: string) => void
    excludeFilter: Map<string, string[]>,
    setExcludeFilter: (filter: Map<string, string[]>) => void
    regexes: string[]
    setRegexes: (regexes: string[]) => void
    excludeRegexes: string[]
    setExcludeRegexes: (regexes: string[]) => void
    logAttributes?: Map<string, Attribute[]>
    dropDownFunction?: (key: string) => Promise<Attribute[]>
    enableDashboardStyle?: boolean
    dialogRef?: React.RefObject<HTMLElement>
    showSavedSearches?: boolean
    isMetoroQLMode?: boolean
    setIsMetoroQLMode?: (isMetoroQLMode: boolean) => void
    metoroQLQuery?: string
    setMetoroQLQuery?: (query: string) => void
    customColumns?: string[]
    setCustomColumns?: (columns: string[]) => void
    showExportLogButton?: boolean
    transforms: Transform[]
    setTransforms: Dispatch<SetStateAction<Transform[]>>
    showAddTransformButton?: boolean
    hideClearFiltersButton?: boolean
    disallowKeys?: string[]
}) {
    // Local state for immediate text field updates
    const [localMetoroQLQuery, setLocalMetoroQLQuery] = useState<string>(props.metoroQLQuery || "");
    const [isTransformVisible, setIsTransformVisible] = useState(false);

    // Update local state when props change
    useEffect(() => {
        setLocalMetoroQLQuery(props.metoroQLQuery || "");
    }, [props.metoroQLQuery]);


    let pills: ReactElement[] = [];
    const entries = Array.from(props.filter.entries());
    for (let i = 0; i < entries.length; i++) {
        const [key, values] = entries[i]
        if (values.length === 0) {
            continue;
        }
        const valuesString = values.join(" || ")
        pills.push(<Pill
            dialogRef={props.dialogRef}
            dropDownFunction={props.dropDownFunction}
            key={i.toString()} attributeKey={key} attributeValue={valuesString} filter={props.filter}
            setFilter={props.setFilter} excludeFilter={props.excludeFilter}
            setExcludeFilter={props.setExcludeFilter}
            isEditable={true}
            regexes={props.regexes}
            setRegexes={props.setRegexes}
            excludeRegexes={props.excludeRegexes}
            setExcludeRegexes={props.setExcludeRegexes}
        />)
    }

    const excludeEntries = Array.from(props.excludeFilter.entries());
    for (let i = 0; i < excludeEntries.length; i++) {
        const [key, values] = excludeEntries[i]
        if (values.length === 0) {
            continue;
        }
        const valuesString = values.join(" || ")
        pills.push(<Pill
            dialogRef={props.dialogRef}
            dropDownFunction={props.dropDownFunction}
            key={i.toString()} attributeKey={key} attributeValue={valuesString}
            filter={props.excludeFilter}
            setFilter={props.setFilter} isExclude={true} excludeFilter={props.excludeFilter}
            setExcludeFilter={props.setExcludeFilter}
            isEditable={true}
            regexes={props.regexes}
            setRegexes={props.setRegexes}
            excludeRegexes={props.excludeRegexes}
            setExcludeRegexes={props.setExcludeRegexes}
        />)
    }

// Create a pill for each exclude regex
    for (let i = 0; i < props.excludeRegexes.length; i++) {
        const regex = props.excludeRegexes[i];
        pills.push(<Pill
            dialogRef={props.dialogRef}
            key={i.toString()} attributeKey={"regex_match"} attributeValue={regex}
            filter={props.excludeFilter}
            setFilter={props.setFilter} excludeFilter={props.excludeFilter}
            setExcludeFilter={props.setExcludeFilter}
            isEditable={true}
            regexes={props.regexes}
            setRegexes={props.setRegexes}
            excludeRegexes={props.excludeRegexes}
            setExcludeRegexes={props.setExcludeRegexes}
            isRegex={true}
            isExclude={true}
        />)
    }

// Create a pill for each regex
    for (let i = 0; i < props.regexes.length; i++) {
        const regex = props.regexes[i];
        pills.push(<Pill
            dialogRef={props.dialogRef}
            key={i.toString()} attributeKey={"regex_match"} attributeValue={regex} filter={props.filter}
            setFilter={props.setFilter} excludeFilter={props.excludeFilter}
            setExcludeFilter={props.setExcludeFilter}
            isEditable={true}
            regexes={props.regexes}
            setRegexes={props.setRegexes}
            excludeRegexes={props.excludeRegexes}
            setExcludeRegexes={props.setExcludeRegexes}
            isRegex={true}
        />)
    }


    return <div className={cn("overflow-y-auto flex flex-col w-full", isTransformVisible && "mb-2")}>
        <div
            className={cn("bg-backgroundmedium flex-none w-full items-center flex", props.embeded ? "" : (!isTransformVisible && props.enableDashboardStyle ? "border-t" : "border rounded hover:border-primary"))}>
            {props.showAddTransformButton && !props.isMetoroQLMode && (!props.transforms || props.transforms.length == 0) &&
                <AddTransformButton isMinimalStyle={true} setTransforms={props.setTransforms}
                                    setIsTransformsVisible={setIsTransformVisible}/>}
            {props.transforms && !props.isMetoroQLMode &&
                <div className={"flex h-full"}>
                    <TransformAttributeToggle
                        setIsVisible={setIsTransformVisible}
                        isVisible={isTransformVisible}
                        transforms={props.transforms}
                        alwaysShowBorder={true}
                    /></div>
            }
            {!props.embeded && (props.enableDashboardStyle ? pills.length == 0 : true) &&
                <div className={cn("pl-2 relative text-border mr-2", props.enableDashboardStyle ? "" : "w-6 h-6")}>
                    <SearchIcon className={cn(props.enableDashboardStyle ? "w-5 h-5" : "w-6 h-6")}/>
                </div>
            }

            {/* Standard filter mode */}
            {!props.isMetoroQLMode && (
                <>
                    <div
                        className={cn("grow max-w-full shrink justify-start items-center gap-2 flex ", props.enableDashboardStyle ? "mx-1 h-[42px] overflow-x-auto no-scrollbar" : "flex-wrap px-[7px] py-[9px]")}>
                        {
                            pills
                        }
                        <div className={"flex grow shrink"}>
                            <InputPill
                                inputKeys={props.logAttributes ? Array.from(props.logAttributes.keys()) : []}
                                placeholderText={""}
                                filter={props.filter} setFilter={props.setFilter}
                                excludeFilter={props.excludeFilter} setExcludeFilter={props.setExcludeFilter}
                                regexes={props.regexes} setRegexes={props.setRegexes}
                                disallowKeys={props.disallowKeys}
                            />
                        </div>
                    </div>
                </>
            )}

            {/* MetoroQL mode */}
            {props.isMetoroQLMode && props.setMetoroQLQuery && (
                <div className="grow flex items-center ml-2">
                    <Textarea
                        value={localMetoroQLQuery}
                        onChange={(e) => {
                            // Update local state immediately for responsive UI
                            const newValue = e.target.value;
                            setLocalMetoroQLQuery(newValue);

                            // Also update parent state (this is debounced in the parent)
                            // so the actual query will only be executed after the user stops typing
                            props.setMetoroQLQuery && props.setMetoroQLQuery(newValue);
                        }}
                        placeholder="Enter MetoroQL query (e.g., count(logs{log_level=&quot;error&quot;}))"
                        className="flex-grow h-[50px] min-h-[50px] bg-backgroundmedium text-textlight border-none border-0 focus-visible:border-0 focus-visible:ring-0 outline-none outline-0 ring-0 overflow-y-auto"
                    />
                </div>
            )}

            <div className={"flex-none flex items-center h-full"}>
                {!props.hideClearFiltersButton && <div className={"h-full w-[1px] bg-border"}/>}
                {!props.hideClearFiltersButton && <Tooltip>
                    <TooltipTrigger asChild>
                        <div onClick={
                            () => {
                                if (props.isMetoroQLMode && props.setMetoroQLQuery) {
                                    // Clear both local state and parent state
                                    setLocalMetoroQLQuery("");
                                    props.setMetoroQLQuery("");
                                } else {
                                    const input = document.getElementById("free_text_input") as HTMLInputElement;
                                    if (input) {
                                        input.value = "";
                                    }
                                    props.setFreeTextFilter("");
                                    props.setFilter(new Map());
                                    props.setExcludeFilter(new Map());
                                    props.setRegexes([]);
                                    props.setExcludeRegexes([]);
                                }
                            }
                        }
                             className="flex items-center w-max relative text-textdark rounded hover:cursor-pointer h-full hover:bg-backgroundlight px-1">
                            <XIcon className={"flex items-center justify-center w-5 h-5 text-textmedium"}/>
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>
                        Clear all filters
                    </TooltipContent>
                </Tooltip> }
                {!props.hideClearFiltersButton &&  <div className={"h-full w-[1px] bg-border"}/>}


                {!props.embeded && props.showSavedSearches && (
                    <div className={"flex h-full"}>
                        <SavedLogSearchesDrawer
                            metricSpecifier={
                                props.isMetoroQLMode ? undefined : {
                                    filters: props.filter,
                                    excludeFilters: props.excludeFilter,
                                    regexes: props.regexes,
                                    excludeRegexes: props.excludeRegexes,
                                    metricName: "logs",
                                    aggregation: "count",
                                    metricType: MetricType.Logs,
                                    functions: [],
                                    transforms: props.transforms,
                                }
                            }
                            metoroQLQuerySpecifier={
                                props.isMetoroQLMode ? {
                                    query: localMetoroQLQuery,
                                } : undefined
                            }
                            setIsMetoroQLMode={props.setIsMetoroQLMode}
                            setMetoroQLQuery={props.setMetoroQLQuery}
                            setLocalMetoroQLQuery={props.setMetoroQLQuery}
                            setFilter={props.setFilter}
                            setExcludeFilter={props.setExcludeFilter}
                            setRegexes={props.setRegexes}
                            setExcludeRegexes={props.setExcludeRegexes}
                            setTransform={props.setTransforms}
                        />
                        <div className={"h-full w-[1px] bg-border"}/>
                    </div>
                )}

                {/* Toggle button between standard and MetoroQL mode */}
                {props.setIsMetoroQLMode && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div
                                onClick={() => {
                                    if (!props.isMetoroQLMode) {
                                        const convertToMetoroQL = async () => {
                                            try {
                                                // Create metric specifier from current filters
                                                const metricSpecifier: MetricSpecifier = {
                                                    metricName: "logs",
                                                    filters: props.filter,
                                                    excludeFilters: props.excludeFilter,
                                                    splits: [],
                                                    aggregation: "count",
                                                    metricType: MetricType.Logs,
                                                    functions: [],
                                                    regexes: props.regexes,
                                                    excludeRegexes: props.excludeRegexes,
                                                    transforms: props.transforms,
                                                };

                                                // Convert to MetoroQL
                                                const response = await convertMetricSpecifierToMetoroQL([metricSpecifier], []);
                                                if (response.queries && response.queries.length > 0) {
                                                    setLocalMetoroQLQuery(response.queries[0]);
                                                    props.setMetoroQLQuery!(response.queries[0]);
                                                }
                                            } catch (error) {
                                                console.error("Error converting filters to MetoroQL:", error);
                                                toast({
                                                    className: "bg-red-500/20 border border-red-500 text-textmedium rounded",
                                                    title: "Conversion error",
                                                    description: "Failed to convert filters to MetoroQL query",
                                                });
                                                // Set a default query
                                                props.setMetoroQLQuery!("count(logs)");
                                                setLocalMetoroQLQuery("count(logs)");
                                            }
                                            props.setFilter(new Map());
                                            props.setExcludeFilter(new Map());
                                            props.setRegexes([]);
                                            props.setExcludeRegexes([]);
                                            props.setTransforms([]);
                                            props.setIsMetoroQLMode!(true);
                                        };
                                        convertToMetoroQL();
                                    } else {
                                        // Convert MetoroQL query to filters
                                        const convertToFilters = async () => {
                                            try {
                                                // Convert from MetoroQL
                                                const response = await convertMetoroQLToMetricSpecifier([props.metoroQLQuery || ""]);

                                                if (response.metricSpecifiers && response.metricSpecifiers.length > 0) {
                                                    const spec = response.metricSpecifiers[0];
                                                    console.log("spec", spec);

                                                    // Update filters
                                                    if (spec.filters) {
                                                        props.setFilter(spec.filters);
                                                    }

                                                    // Update exclude filters
                                                    if (spec.excludeFilters) {
                                                        props.setExcludeFilter(spec.excludeFilters);
                                                    }

                                                    // Update regexes
                                                    if (spec.regexes) {
                                                        props.setRegexes(spec.regexes);
                                                    }

                                                    // Update exclude regexes
                                                    if (spec.excludeRegexes) {
                                                        props.setExcludeRegexes(spec.excludeRegexes);
                                                    }

                                                    console.log(spec.transforms)

                                                    if (spec.transforms) {
                                                        props.setTransforms(spec.transforms);
                                                    }
                                                }
                                            } catch (error) {
                                                console.error("Error converting MetoroQL to filters:", error);
                                                toast({
                                                    className: "bg-red-500/20 border border-red-500 text-textmedium rounded",
                                                    title: "Conversion error",
                                                    description: "Failed to convert MetoroQL query to filters",
                                                });
                                            }
                                        };
                                        props.setIsMetoroQLMode!(false);
                                        props.setMetoroQLQuery!("");
                                        convertToFilters();
                                    }
                                }}
                                className="flex items-center justify-center text-textmedium hover:text-textlight hover:bg-backgroundlight h-full rounded cursor-pointer px-1"
                            >
                                {props.isMetoroQLMode ?
                                    <FormInputIcon className="w-5 h-5"/> :
                                    <CodeIcon className="w-5 h-5"/>
                                }
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            {props.isMetoroQLMode ? "Switch to Standard mode" : "Switch to MetoroQL mode"}
                        </TooltipContent>
                    </Tooltip>
                )}

                {/* Log Export Button */}
                {props.showExportLogButton && <div className={"h-full w-[1px] bg-border"}/>}
                {props.showExportLogButton && <LogExportButton
                    filter={props.filter}
                    excludeFilter={props.excludeFilter}
                    regexes={props.regexes}
                    excludeRegexes={props.excludeRegexes}
                    transforms={props.transforms}
                    isMetoroQLMode={props.isMetoroQLMode || false}
                    metoroQLQuery={props.metoroQLQuery || ""}
                    customColumns={props.customColumns}
                />}
            </div>
        </div>
        {isTransformVisible &&
            <div className={"flex flex-col gap-2"}>
                <div className={"flex flex-col"}>
                    {props.transforms?.map((transform, index) => {
                        return <TransformSelector
                            key={`transform-${index}`}
                            transform={transform}
                            setTransforms={props.setTransforms}
                        />
                    })}
                </div>
                {props.showAddTransformButton && (!props.transforms || props.transforms.length > 0) &&
                    <AddTransformButton isMinimalStyle={false} setTransforms={props.setTransforms}
                                        setIsTransformsVisible={setIsTransformVisible}/>}
            </div>
        }
    </div>
}

// Log Export Button Component
interface LogExportButtonProps {
    filter: Map<string, string[]>;
    excludeFilter: Map<string, string[]>;
    regexes: string[];
    excludeRegexes: string[];
    transforms: Transform[];
    isMetoroQLMode: boolean;
    metoroQLQuery: string;
    customColumns?: string[];
}

function LogExportButton(props: LogExportButtonProps) {
    const [open, setOpen] = useState(false);
    const [exportLimit, setExportLimit] = useState(100);
    const timeRange = useSelector(timerange.selectors.getTimeRange)
    const [environments, setEnvironments] = useState<string[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        let environments = searchParams.get("environment");
        if (environments !== null) {
            setEnvironments([environments])
        }
    }, [searchParams])

    const handleExport = async () => {
        try {
            // Create the request payload
            const request = {
                startTime: Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
                endTime: Math.floor(timeRange.getStartEnd()[1].getTime() / 1000),
                filters: Object.fromEntries(props.filter),
                excludeFilters: Object.fromEntries(props.excludeFilter),
                regexes: props.regexes,
                transforms: props.transforms ? props.transforms : [],
                excludeRegexes: props.excludeRegexes,
                ascending: true,
                metoroQLQuery: props.isMetoroQLMode ? props.metoroQLQuery : "",
                isExport: true,
                exportLimit: exportLimit,
                environments: environments[0] === "" ? [] : environments,
                customColumns: props.customColumns || [],
            };

            // Make the API call
            const response = await axios.post('/api/v1/logs', request, {
                responseType: 'blob', // Important for handling file downloads
            });

            // Create a download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'logs_export.csv';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename=(.*)/);
                if (filenameMatch && filenameMatch.length > 1) {
                    filename = filenameMatch[1];
                }
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            // Clean up
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);

            // Close the dialog
            setOpen(false);

            // Show success toast
            toast({
                title: "Logs exported successfully",
                description: `Exported ${exportLimit} logs to ${filename}`,
            });
        } catch (error) {
            console.error("Error exporting logs:", error);
            toast({
                className: "bg-red-500/20 border border-red-500 text-textmedium rounded",
                title: "Export error",
                description: "Failed to export logs",
            });
        }
    };

    // Format the time range for display
    const formatTimeRange = () => {
        const startTime = new Date(Math.floor(timeRange.getStartEnd()[0].getTime() / 1000));
        const endTime = new Date(Math.floor(timeRange.getStartEnd()[1].getTime() / 1000));
        const formattedStartTime = displayDateTimeWithSelectedFormat(
            startTime,
            [DateTimePrecision.Month, DateTimePrecision.Day, DateTimePrecision.Hours, DateTimePrecision.Minutes, DateTimePrecision.Seconds]
        );
        const formattedEndTime = displayDateTimeWithSelectedFormat(
            endTime,
            [DateTimePrecision.Month, DateTimePrecision.Day, DateTimePrecision.Hours, DateTimePrecision.Minutes, DateTimePrecision.Seconds]
        );
        return `${formattedStartTime} to ${formattedEndTime}`;
    };

    // Format the query for display
    const formatQuery = () => {
        if (props.isMetoroQLMode) {
            return props.metoroQLQuery || "No query";
        } else {
            const filters = Array.from(props.filter.entries())
                .map(([key, values]) => `${key}: ${values.join(" || ")}`)
                .join(", ");
            const excludeFilters = Array.from(props.excludeFilter.entries())
                .map(([key, values]) => `NOT ${key}: ${values.join(" || ")}`)
                .join(", ");
            const regexMatches = props.regexes.map(regex => `REGEX: ${regex}`).join(", ");
            const excludeRegexMatches = props.excludeRegexes.map(regex => `NOT REGEX: ${regex}`).join(", ");

            const parts = [filters, excludeFilters, regexMatches, excludeRegexMatches].filter(part => part.length > 0);
            return parts.length > 0 ? parts.join(", ") : "No filters";
        }
    };

    // Format the transforms for display
    const formatTransforms = () => {
        if (!props.transforms || props.transforms.length === 0) {
            return "No transforms";
        }

        return props.transforms.map((transform, index) => {
            if (transform.type === TransformTypes.Attribute && transform.transformAttribute) {
                return `${index + 1}. ${transform.transformAttribute.transformationFunction} → ${transform.transformAttribute.outputAttributeKey}`;
            }
            return `${index + 1}. Unknown transform type`;
        }).join("\n");
    };

    // Format the custom columns for display
    const formatCustomColumns = () => {
        if (!props.customColumns || props.customColumns.length === 0) {
            return "No custom columns";
        }

        return props.customColumns.join(", ")
    };

    return (
        <>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        onClick={() => setOpen(true)}
                        className="flex items-center justify-center text-textmedium hover:text-textlight hover:bg-backgroundlight h-full rounded cursor-pointer px-1"
                    >
                        <DownloadIcon className="w-5 h-5"/>
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    Export Logs
                </TooltipContent>
            </Tooltip>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-[600px] text-textmedium">
                    <DialogHeader>
                        <DialogTitle>Export Logs</DialogTitle>
                        <DialogDescription>
                            Confirm the details below to export logs.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="timerange" className="text-right">
                                Time Range
                            </Label>
                            <div id="timerange" className="col-span-3 text-textmedium">
                                {formatTimeRange()}
                            </div>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="query" className="text-right">
                                Query
                            </Label>
                            <div id="query" className="col-span-3 text-textmedium overflow-auto max-h-[100px]">
                                {formatQuery()}
                            </div>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="transforms" className="text-right">
                                Transforms
                            </Label>
                            <div id="transforms" className="col-span-3 text-textmedium overflow-auto max-h-[100px] whitespace-pre-line">
                                {formatTransforms()}
                            </div>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="customColumns" className="text-right">
                                Custom Columns
                            </Label>
                            <div id="customColumns" className="col-span-3 text-textmedium overflow-auto max-h-[100px] whitespace-pre-line">
                                {formatCustomColumns()}
                            </div>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="limit" className="text-right">
                                Limit
                            </Label>
                            <Input
                                id="limit"
                                type="number"
                                value={exportLimit}
                                onChange={(e) => setExportLimit(parseInt(e.target.value) || 100)}
                                className="col-span-3"
                                min={1}
                                max={10000}
                            />
                        </div>
                    </div>
                    <DialogFooter className={"h-[36px]"}>
                        <div
                            className={"bg-red-500/20 border-red-500 flex items-center justify-center px-2 border rounded hover:cursor-pointer hover:text-textlight"}
                            onClick={() => setOpen(false)}>
                            Cancel
                        </div>
                        <div
                            className={"bg-primarytransparent border-primary flex items-center justify-center px-2 border rounded hover:cursor-pointer hover:text-textlight"}
                            onClick={handleExport}>
                            Export Logs
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function escapeHtml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function LogView(props: {
    removeService?: boolean,
    removeTime?: boolean,
    regex: string,
    log: Log
    filter: Map<string, string[]>,
    setFilter: (filter: Map<string, string[]>) => void,
    excludeFilter: Map<string, string[]>,
    setExcludeFilter: (filter: Map<string, string[]>) => void
    regexes: string[]
    setRegexes: (regexes: string[]) => void
    excludeRegexes: string[]
    setExcludeRegexes: (regexes: string[]) => void
    isFilterShown: boolean
    wrapLogs: boolean
    customColumns?: string[]
    setCustomColumns?: (columns: string[]) => void
}) {
    let indicatorColor = getLogLeveLIndicatorColor(props.log.severity);
    let match = new RegExp("");
    try {
        match = new RegExp(props.regex);
    } catch (e) {
        console.error(e)
    }
    const [open, setOpen] = React.useState(false);

    const logMsgToShow = props.log.message === "" ? JSON.stringify(Object.fromEntries(props.log.logAttributes)) : props.log.message
    const messageContent = props.regex !== ""
        ? escapeHtml(logMsgToShow).replace(match, '<mark class="highlight">$&</mark>')
        : escapeHtml(logMsgToShow);

    // Format the timestamp once
    const formattedTime = displayDateTimeWithSelectedFormat(
        new Date(props.log.time),
        [DateTimePrecision.Month, DateTimePrecision.Day, DateTimePrecision.Hours, DateTimePrecision.Minutes, DateTimePrecision.Seconds, DateTimePrecision.Milliseconds]
    );

    if (props.wrapLogs) {
        // For wrapped mode, use a completely different layout
        return (
            <Drawer.Root modal={false} direction="right" open={open} onOpenChange={setOpen}>
                <Drawer.Trigger asChild>
                    <div id="clickableLogLine"
                         className="w-full hover:bg-backgroundlight cursor-pointer"
                    >
                        {/* Single row with all content aligned */}
                        <div className="flex px-2 py-2 items-stretch">
                            <div className="flex-shrink-0 self-stretch flex items-stretch mr-2">
                                <div className={cn(indicatorColor, "w-[4px] rounded")}></div>
                            </div>

                            {!props.removeTime &&
                                <div className="flex-none w-[160px] text-textmedium text-sm font-medium">
                                    {formattedTime}
                                </div>
                            }

                            {!props.removeService &&
                                <div className={cn(
                                    "flex-none w-[160px] text-textmedium text-sm font-medium truncate",
                                    getBreakpointAttributesForLogs(props.isFilterShown, "service")
                                )}>
                                    {props.log.serviceName}
                                </div>
                            }
                            {props.customColumns && props.customColumns.length > 0 && props.customColumns.map((column, index) => (
                                <div key={index}
                                     className="flex-none w-[160px] text-textmedium text-sm font-medium truncate">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="truncate max-w-full">
                                                {props.log.logAttributes.get(column) || props.log.resourceAttributes.get(column) || ""}
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {props.log.logAttributes.get(column) || props.log.resourceAttributes.get(column) || ""}
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                            ))}
                            {/* Message content inline with the other elements */}
                            <div
                                dangerouslySetInnerHTML={{__html: messageContent.replace(/\n/g, '<br/>')}}
                                className={cn(
                                    "text-textmedium text-sm font-medium whitespace-pre-wrap break-words",
                                    getBreakpointAttributesForLogs(props.isFilterShown, "message")
                                )}>
                            </div>
                        </div>
                    </div>
                </Drawer.Trigger>
                <Drawer.Portal>
                    <Drawer.Content
                        onInteractOutside={() => setOpen(false)}
                        className="select-text flex border-l flex-col h-full w-2/3 mt-24 fixed bottom-0 right-0 z-10">
                        <LogPanelContent
                            log={props.log}
                            filter={props.filter}
                            setFilter={props.setFilter}
                            excludeFilter={props.excludeFilter}
                            setExcludeFilter={props.setExcludeFilter}
                            regexes={props.regexes}
                            setRegexes={props.setRegexes}
                            excludeRegexes={props.excludeRegexes}
                            setExcludeRegexes={props.setExcludeRegexes}
                            customColumns={props.customColumns}
                            setCustomColumns={props.setCustomColumns}
                        />
                    </Drawer.Content>
                </Drawer.Portal>
            </Drawer.Root>
        );
    }

    // Standard non-wrapped view (unchanged)
    return <Drawer.Root modal={false} direction="right" open={open} onOpenChange={setOpen}>
        <Drawer.Trigger asChild>
            <div id="clickableLogLine"
                 className="w-full max-w-full h-8 px-2 justify-start items-center flex hover:bg-backgroundlight overflow-x-clip"
            >
                <div className={cn(indicatorColor, "flex-none w-[4px] h-6 rounded")}/>
                <div
                    className="w-full h-8 px-2 justify-start items-center flex gap-4 hover:bg-backgroundlight overflow-x-clip hover:cursor-pointer">
                    {!props.removeTime && <div className={cn(
                        "flex-none flex justify-start items-start space-x-2", props.removeTime ? "" : "w-[152px]",
                    )}>
                        <div
                            className="flex-none w-full text-textmedium text-sm font-medium truncate">
                            {formattedTime}
                        </div>
                    </div>}
                    {!props.removeService && <div
                        className={cn(
                            "flex-none w-[160px] text-textmedium text-sm font-medium truncate",
                            getBreakpointAttributesForLogs(props.isFilterShown, "service")
                        )}>
                        {
                            props.log.serviceName
                        }
                    </div>}
                    {props.customColumns && props.customColumns.length > 0 && props.customColumns.map((column, index) => (
                        <div key={index} className="flex-none w-[160px] text-textmedium text-sm font-medium truncate">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="truncate max-w-full">
                                        {props.log.logAttributes.get(column) || props.log.resourceAttributes.get(column) || ""}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {props.log.logAttributes.get(column) || props.log.resourceAttributes.get(column) || ""}
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    ))}
                    <div
                        dangerouslySetInnerHTML={{__html: messageContent}}
                        className={cn(
                            "text-textmedium text-sm font-medium grow truncate leading-[16px] overflow-y-clip",
                            getBreakpointAttributesForLogs(props.isFilterShown, "message")
                        )}>
                    </div>
                </div>
            </div>
        </Drawer.Trigger>
        <Drawer.Portal>
            <Drawer.Content
                onInteractOutside={() => setOpen(false)}
                className="select-text flex border-l flex-col h-full w-2/3 mt-24 fixed bottom-0 right-0 z-10">
                <LogPanelContent
                    log={props.log}
                    filter={props.filter}
                    setFilter={props.setFilter}
                    excludeFilter={props.excludeFilter}
                    setExcludeFilter={props.setExcludeFilter}
                    regexes={props.regexes}
                    setRegexes={props.setRegexes}
                    excludeRegexes={props.excludeRegexes}
                    setExcludeRegexes={props.setExcludeRegexes}
                    customColumns={props.customColumns}
                    setCustomColumns={props.setCustomColumns}
                />
            </Drawer.Content>
        </Drawer.Portal>
    </Drawer.Root>
}


function getBreakpointAttributesForLogs(isFilterShown: boolean, componentName: string) {
    if (isFilterShown) {
        switch (componentName) {
            case "service":
                return " max-[900px]:hidden"
        }
    } else {
        switch (componentName) {
            case "service":
                return " max-[620px]:hidden"
        }
    }
}

function LogFeed(props: {
    ascending?: boolean,
    setAscending?: (ascending: boolean) => void,
    logs: Log[],
    scrolledToBottomHandler: () => void,
    removeService?: boolean,
    removeTime?: boolean,
    filter: Map<string, string[]>,
    setFilter: (filter: Map<string, string[]>) => void,
    excludeFilter: Map<string, string[]>,
    setExcludeFilter: (filter: Map<string, string[]>) => void
    regexes: string[]
    setRegexes: (regexes: string[]) => void
    excludeRegexes: string[]
    setExcludeRegexes: (regexes: string[]) => void
    showExpand?: boolean
    expandOnClick?: () => void,
    isFilterShown: boolean
    enableDashboardStyle?: boolean
    customColumns?: string[]
    setCustomColumns?: (columns: string[]) => void
}) {
    const [wrapLogs, setWrapLogs] = useState<boolean>(false);
    const [customColumnInput, setCustomColumnInput] = useState<string>('');
    const handleScroll = (e: any) => {
        const bottom = Math.abs(e.target.scrollHeight - (e.target.scrollTop + e.target.clientHeight)) <= 1
        if (bottom) {
            props.scrolledToBottomHandler();
        }
    }

    let overrideTextFont = props.enableDashboardStyle ? "text-base" : "text-lg"
    return <div
        className={cn("min-w-0 bg-backgroundmedium rounded min-h-0 w-full flex flex-col grow shrink", props.enableDashboardStyle ? "border-t" : "border")}>
        <div className={"flex justify-end pl-2"}>
            <div
                className={cn("w-full flex-none px-2 py-1 rounded-tl rounded-tr justify-start items-center gap-4 flex grow shrink overflow-x-hidden", props.enableDashboardStyle ? "h-[36px]" : "h-[48px]")}>
                {!props.removeTime &&
                    <div
                        className={`w-[152px] h-full flex items-center justify-between font-normal text-textmedium ${overrideTextFont}`}>
                        <div>Time</div>
                        <div onClick={() => {
                            if (props.setAscending) {
                                props.setAscending(!props.ascending)
                            }
                        }} className="hover:cursor-pointer text-sm flex flex-col justify-center pr-4">
                            {props.ascending ? "▲" : "▼"}
                        </div>
                    </div>
                }
                {!props.removeService &&
                    <div
                        className={`w-[160px] flex h-full font-normal items-center text-textmedium ${overrideTextFont} ${getBreakpointAttributesForLogs(props.isFilterShown, "service")}`}>Service
                    </div>}
                {props.customColumns && props.customColumns.length > 0 && props.customColumns.map((column, index) => (
                    <div key={index}
                         className={`w-[160px] flex h-full font-normal items-center text-textmedium ${overrideTextFont}`}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="truncate max-w-full">
                                    {column}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                {column}
                            </TooltipContent>
                        </Tooltip>
                    </div>
                ))}
                <div className="flex justify-between items-center grow">
                    <div
                        className={` text-textmedium ${overrideTextFont} ${getBreakpointAttributesForLogs(props.isFilterShown, "message")}`}>Message
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 text-textmedium">
                            <Checkbox
                                checked={wrapLogs}
                                onCheckedChange={(checkedState) => setWrapLogs(checkedState === "indeterminate" ? false : checkedState)}
                                className="h-4 w-4"
                            />
                            <span className="text-sm">Wrap</span>
                        </label>

                        {props.customColumns !== undefined && props.setCustomColumns !== undefined && (
                            <>
                                {props.logs && props.logs.length > 0 && (
                                    <Popover>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        size="sm"
                                                        className="ml-2 flex items-center gap-1 text-textmedium hover:bg-backgroundlight bg-transparent"
                                                    >
                                                        <Columns3 className="h-4 w-4"/>
                                                        <span className="sr-only">Manage columns</span>
                                                    </Button>
                                                </PopoverTrigger>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Manage columns
                                            </TooltipContent>
                                        </Tooltip>
                                        <PopoverContent
                                            className="w-[300px] p-0 text-textmedium bg-backgrounddark"
                                            align="end"
                                            sideOffset={8}
                                            alignOffset={-8}
                                        >
                                            <div className="p-2 border-b">
                                                <h4 className="font-medium text-sm">Manage Columns</h4>
                                                <p className="text-xs text-textdark mt-1">
                                                    Select columns to display in the logs table. Maximum 4 custom
                                                    columns allowed.
                                                </p>
                                            </div>
                                            <Command className="rounded shadow-md">
                                                <CommandInput
                                                    placeholder="Search or add columns..."
                                                    value={customColumnInput}
                                                    onValueChange={setCustomColumnInput}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && customColumnInput.trim() && props.setCustomColumns && props.customColumns && !props.customColumns.includes(customColumnInput.trim())) {
                                                            e.preventDefault();
                                                            if (props.customColumns.length < 4) {
                                                                props.setCustomColumns([...props.customColumns, customColumnInput.trim()]);
                                                                setCustomColumnInput('');
                                                            } else {
                                                                toast({
                                                                    title: "Column limit reached",
                                                                    description: "You can add a maximum of 4 custom columns.",
                                                                    variant: "destructive",
                                                                });
                                                            }
                                                        }
                                                    }}
                                                />
                                                <CommandList>
                                                    <CommandEmpty>
                                                        {customColumnInput && (
                                                            <div className="p-2">
                                                                <p className="text-xs text-textdark mb-2">
                                                                    No matching columns found. Press Enter to add:
                                                                </p>
                                                                <Button
                                                                    size="sm"
                                                                    className="w-full"
                                                                    onClick={() => {
                                                                        if (customColumnInput.trim() && props.setCustomColumns && props.customColumns && !props.customColumns.includes(customColumnInput.trim())) {
                                                                            if (props.customColumns.length < 4) {
                                                                                props.setCustomColumns([...props.customColumns, customColumnInput.trim()]);
                                                                                setCustomColumnInput('');
                                                                            } else {
                                                                                toast({
                                                                                    title: "Column limit reached",
                                                                                    description: "You can add a maximum of 4 custom columns.",
                                                                                    variant: "destructive",
                                                                                });
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    Add "{customColumnInput}"
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </CommandEmpty>
                                                    <CommandGroup
                                                        heading="Selected Columns"
                                                        className="[&_[cmdk-group-heading]]:bg-backgroundlight [&_[cmdk-group-heading]]:text-textmedium [&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:border-b [&_[cmdk-group-heading]]:border-border"
                                                    >
                                                        {props.customColumns && props.customColumns.length > 0 ? (
                                                            [...props.customColumns].sort().map((column) => (
                                                                <CommandItem
                                                                    key={`selected-${column}`}
                                                                    onSelect={() => {
                                                                        if (props.setCustomColumns && props.customColumns) {
                                                                            props.setCustomColumns(
                                                                                props.customColumns.filter(col => col !== column)
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    <div
                                                                        className="flex items-center justify-between w-full hover:cursor-pointer hover:text-textdark">
                                                                        <span>{column}</span>
                                                                        <XIcon className="h-4 w-4"/>
                                                                    </div>
                                                                </CommandItem>
                                                            ))
                                                        ) : (
                                                            <div className="py-2 px-2 text-xs text-textdark">
                                                                No columns selected
                                                            </div>
                                                        )}
                                                    </CommandGroup>
                                                    <CommandGroup
                                                        heading="Available Columns"
                                                        className="[&_[cmdk-group-heading]]:bg-backgroundlight [&_[cmdk-group-heading]]:text-textmedium [&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:border-b [&_[cmdk-group-heading]]:border-t [&_[cmdk-group-heading]]:border-border"
                                                    >
                                                        {Array.from(new Set(
                                                            props.logs.flatMap(log =>
                                                                [...Array.from(log.logAttributes.keys()), ...Array.from(log.resourceAttributes.keys())]
                                                            )
                                                        ))
                                                            .filter(key => props.customColumns ? !props.customColumns.includes(key) : true)
                                                            .sort()
                                                            .map((column) => (
                                                                <CommandItem
                                                                    key={`available-${column}`}
                                                                    onSelect={() => {
                                                                        if (props.setCustomColumns && props.customColumns) {
                                                                            if (props.customColumns.length < 4) {
                                                                                props.setCustomColumns([...props.customColumns, column]);
                                                                            } else {
                                                                                toast({
                                                                                    title: "Column limit reached",
                                                                                    description: "You can add a maximum of 4 custom columns.",
                                                                                    variant: "destructive",
                                                                                });
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    <div
                                                                        className="flex items-center w-full hover:cursor-pointer hover:bg-primarytransparent px-1 py-1 hover:border hover:border-primary rounded">
                                                                        <span>{column}</span>
                                                                    </div>
                                                                </CommandItem>
                                                            ))}
                                                    </CommandGroup>
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            {props.showExpand && props.expandOnClick &&
                <Maximize2Icon
                    className={"w-4 h-4 text-textdark border-l border-b rounded-bl hover:text-primary hover:cursor-pointer"}
                    onClick={props.expandOnClick}/>
            }
        </div>
        <div
            className="h-0 max-w-full min-w-0 min-h-0 overflow-y-auto scrollMedium border-t rounded-b flex flex-col grow shrink"
            onScroll={handleScroll}>
            {
                props.logs.length === 0 &&
                <div className="flex-grow flex justify-center items-center">
                    <div className="text-textdark text-lg">No logs found</div>
                </div>
            }
            {
                props.logs.map((log, index) => {
                    return <LogView
                        regex={props.regexes !== undefined && props.regexes.length > 0 ? props.regexes[0] : ""}
                        removeService={props.removeService}
                        removeTime={props.removeTime}
                        key={index}
                        log={log}
                        filter={props.filter}
                        setFilter={props.setFilter}
                        excludeFilter={props.excludeFilter}
                        setExcludeFilter={props.setExcludeFilter}
                        regexes={props.regexes}
                        setRegexes={props.setRegexes}
                        excludeRegexes={props.excludeRegexes}
                        setExcludeRegexes={props.setExcludeRegexes}
                        isFilterShown={props.isFilterShown}
                        wrapLogs={wrapLogs}
                        customColumns={props.customColumns}
                        setCustomColumns={props.setCustomColumns}
                    />
                })
            }
        </div>
    </div>
}

export function LogPanel(props: {
    ascending: boolean,
    setAscending: (ascending: boolean) => void,
    logs: Logs,
    filter: Map<string, string[]>,
    setFilter: (filter: Map<string, string[]>) => void,
    scrolledToBottomHandler: (() => void),
    setFreeTextFilter: (filter: string) => void
    excludeFilter: Map<string, string[]>
    setExcludeFilter: (filter: Map<string, string[]>) => void
    regexes: string[]
    setRegexes: (regexes: string[]) => void
    excludeRegexes: string[]
    setExcludeRegexes: (regexes: string[]) => void
    isFilterShown: boolean
    hideSearchBar?: boolean
    removeService?: boolean
    removeTime?: boolean
    dropDownFunction?: (key: string) => Promise<Attribute[]>
    enableDashboardStyle?: boolean
    dialogRef?: React.RefObject<HTMLElement>
    showSavedSearches?: boolean
    isMetoroQLMode?: boolean
    setIsMetoroQLMode?: (isMetoroQLMode: boolean) => void
    metoroQLQuery?: string
    setMetoroQLQuery?: (query: string) => void
    customColumns?: string[]
    setCustomColumns?: (columns: string[]) => void
    transforms: Transform[]
    setTransforms: Dispatch<SetStateAction<Transform[]>>
}) {
    return <div className={"min-w-0 min-h-0 grow shrink w-full flex flex-col"}>
        {(props.hideSearchBar == undefined || !props.hideSearchBar) && <LogSearch
            showSavedSearches={props.showSavedSearches}
            dialogRef={props.dialogRef}
            dropDownFunction={props.dropDownFunction}
            logAttributes={props.logs.filterAttributes}
            setFreeTextFilter={props.setFreeTextFilter}
            filter={props.filter} setFilter={props.setFilter}
            excludeFilter={props.excludeFilter} setExcludeFilter={props.setExcludeFilter}
            regexes={props.regexes} setRegexes={props.setRegexes}
            excludeRegexes={props.excludeRegexes} setExcludeRegexes={props.setExcludeRegexes}
            enableDashboardStyle={props.enableDashboardStyle}
            isMetoroQLMode={props.isMetoroQLMode}
            setIsMetoroQLMode={props.setIsMetoroQLMode}
            metoroQLQuery={props.metoroQLQuery}
            setMetoroQLQuery={props.setMetoroQLQuery}
            customColumns={props.customColumns}
            setCustomColumns={props.setCustomColumns}
            showExportLogButton={true}
            transforms={props.transforms}
            setTransforms={props.setTransforms}
            showAddTransformButton={true}
        />}
        <div
            className={cn("min-w-0 min-h-0 grow shrink w-full flex flex-col", props.enableDashboardStyle ? "" : "mt-4")}>
            <LogFeed
                ascending={props.ascending} setAscending={props.setAscending}
                logs={props.logs.logs}
                scrolledToBottomHandler={props.scrolledToBottomHandler}
                filter={props.filter} setFilter={props.setFilter}
                excludeFilter={props.excludeFilter} setExcludeFilter={props.setExcludeFilter}
                regexes={props.regexes} setRegexes={props.setRegexes}
                excludeRegexes={props.excludeRegexes} setExcludeRegexes={props.setExcludeRegexes}
                showExpand={true}
                isFilterShown={props.isFilterShown}
                removeService={props.removeService}
                removeTime={props.removeTime}
                enableDashboardStyle={props.enableDashboardStyle}
                customColumns={props.customColumns}
                setCustomColumns={props.setCustomColumns}
            />
        </div>
    </div>
}

function MainLogsView(props: {
    ascending: boolean,
    setAscending: (ascending: boolean) => void,
    isLogsLoading?: boolean,
    isFilterAttributesLoading?: boolean,
    logs: Logs,
    setFilter: (filter: Map<string, string[]>) => void,
    filter: Map<string, string[]>,
    scrolledToBottomHandler: (() => void)
    setFreeTextFilter: (filter: string) => void
    setExcludeFilter: (filter: Map<string, string[]>) => void
    excludeFilter: Map<string, string[]>
    regexes: string[]
    setRegexes: (regexes: string[]) => void
    excludeRegexes: string[]
    setExcludeRegexes: (regexes: string[]) => void
    setIsOpened?: React.Dispatch<React.SetStateAction<Map<string, boolean>>>
    dropDownFunction?: (key: string) => Promise<Attribute[]>
    hideSearchBar?: boolean
    dialogRef?: React.RefObject<HTMLElement>
    isMetoroQLMode?: boolean
    setIsMetoroQLMode?: (isMetoroQLMode: boolean) => void
    metoroQLQuery?: string
    setMetoroQLQuery?: (query: string) => void
    customColumns?: string[]
    setCustomColumns?: (columns: string[]) => void
    transforms: Transform[]
    setTransforms: Dispatch<SetStateAction<Transform[]>>
}) {
    const [shrinkFilter, setShrinkFilter] = useState<boolean>(false);

    return <div className={"w-full min-w-0 min-h-0 flex justify-between grow shrink"}>
        {/* Only show FilterPanel when not in MetoroQL mode */}
        {!props.isMetoroQLMode && (
            <div className={"flex flex-none relative"}>
                {!shrinkFilter && props.isFilterAttributesLoading &&
                    <LoadingSpinner className={`absolute top-1/2 left-1/2 z-40"}`}/>}
                <FilterPanel
                    initiallyOpenFilterKeys={["service.name"]}
                    attributes={props.logs.filterAttributes} setFilter={props.setFilter} filter={props.filter}
                    telemetryFiltersComponent={
                        <TelemetryLevelFilters filter={props.filter} setFilter={props.setFilter}
                                               attributes={props.logs.filterAttributes}
                                               excludeFilter={props.excludeFilter}
                                               setExcludeFilter={props.setExcludeFilter}
                                               levelAttributeKey={"log_level"}
                                               indicatorColourFunction={getLogLeveLIndicatorColor}
                        />}
                    filteringCriteria={"log_level"}
                    excludeFilter={props.excludeFilter} setExcludeFilter={props.setExcludeFilter}
                    setFilterShrank={setShrinkFilter}
                    setIsOpened={props.setIsOpened}
                />
            </div>
        )}
        <div className={cn("flex grow shrink min-w-0 min-h-0 relative", !props.isMetoroQLMode ? "pl-4" : "")}>
            {props.isLogsLoading && <LoadingSpinner className={`absolute top-1/2 left-1/2 z-40"}`}/>}
            <LogPanel
                showSavedSearches={true}
                dropDownFunction={props.dropDownFunction}
                ascending={props.ascending}
                setAscending={props.setAscending}
                setFreeTextFilter={props.setFreeTextFilter}
                filter={props.filter} setFilter={props.setFilter}
                logs={props.logs}
                scrolledToBottomHandler={props.scrolledToBottomHandler}
                excludeFilter={props.excludeFilter}
                setExcludeFilter={props.setExcludeFilter}
                regexes={props.regexes}
                setRegexes={props.setRegexes}
                excludeRegexes={props.excludeRegexes}
                setExcludeRegexes={props.setExcludeRegexes}
                isFilterShown={!shrinkFilter}
                hideSearchBar={props.hideSearchBar}
                dialogRef={props.dialogRef}
                isMetoroQLMode={props.isMetoroQLMode}
                setIsMetoroQLMode={props.setIsMetoroQLMode}
                metoroQLQuery={props.metoroQLQuery}
                setMetoroQLQuery={props.setMetoroQLQuery}
                customColumns={props.customColumns}
                setCustomColumns={props.setCustomColumns}
                transforms={props.transforms}
                setTransforms={props.setTransforms}
            />
        </div>
    </div>
}

class LogsResponse {
    constructor(logs: Log[]) {
        this.logs = logs;
    }

    logs: Log[]
}


interface LogSummaryResponse {
    attributes: Map<string, Attribute[]>
}

interface LogsProps {
    justLogsFeed?: boolean
    removeService?: boolean
    expandOnClick?: () => void
    service?: string
    noBaseView?: boolean
    hideSearchBar?: boolean
}

async function updateLogMetrics(props: LogsProps, environments: string[], timeRange: TimeRange, filter: Map<string, string[]>, excludeFilter: Map<string, string[]>, regexes: string[], excludeRegexes: string[], setLogTimeMetrics: React.Dispatch<SetStateAction<TimelineViewerProps | undefined>>, setIsTimelineLoading: (value: (((prevState: boolean) => boolean) | boolean)) => void,
                                updateLogMetricsAbortController: AbortController,
                                setLogTimeMetricsAbortController: React.Dispatch<React.SetStateAction<AbortController>>,
                                transforms: Transform[],
                                isMetoroQLMode?: boolean,
                                metoroQLQuery?: string,
) {
    if (props.justLogsFeed) {
        // In the case where it's just the trace feed, we don't need the metrics
        return;
    }
    const startEnd = timeRange.getStartEnd();

    // Create the timeline props
    const timelineProps: TimelineViewerProps = {
        title: "Log Count",
        startTime: Math.floor(startEnd[0].getTime() / 1000),
        endTime: Math.floor(startEnd[1].getTime() / 1000)
    };

    // If in MetoroQL mode and we have a query, add it to the props
    if (isMetoroQLMode && metoroQLQuery) {
        timelineProps.metoroQLQuery = metoroQLQuery;
    } else {
        // Otherwise use the standard metricSpecifier
        timelineProps.metricSpecifier = {
            aggregation: "count",
            metricName: "log_count",
            metricType: MetricType.Logs,
            filters: filter,
            excludeFilters: excludeFilter,
            regexes: regexes,
            excludeRegexes: excludeRegexes,
            functions: [],
            splits: ["log_level"],
            transforms: transforms
        };
    }

    setLogTimeMetrics(timelineProps)
}

async function updateLogs(setIsLogsLoading: (value: (((prevState: boolean) => boolean) | boolean)) => void, filter: Map<string, string[]>, timeRange: TimeRange, prevMaxTime: number | undefined, excludeFilter: Map<string, string[]>, regexes: string[], excludeRegexes: string[], ascending: boolean, environments: string[], setLogs: (value: (((prevState: Logs) => Logs) | Logs)) => void,
                          abortController: AbortController | undefined, setAbortController: (value: (((prevState: (AbortController | undefined)) => (AbortController | undefined)) | AbortController | undefined)) => void, transforms: Transform[],
                          isMetoroQLMode?: boolean, metoroQLQuery?: string,
) {
    try {
        setIsLogsLoading(true)
        const filters = Object.fromEntries(filter);
        const startEnd = timeRange.getStartEnd();
        let prevEndTime = prevMaxTime;
        const excludeFilterToUse = Object.fromEntries(excludeFilter);
        if (abortController) {
            abortController.abort();
        }
        const abort = new AbortController();
        setAbortController(abort);

        // Create request payload
        const requestPayload: any = {
            "startTime": Math.floor(startEnd[0].getTime() / 1000),
            "endTime": Math.floor(startEnd[1].getTime() / 1000),
            "prevEndTime": prevEndTime,
            "ascending": ascending,
            "environments": environments[0] === "" ? [] : environments
        };

        // If in MetoroQL mode and we have a query, add it to the request
        if (isMetoroQLMode && metoroQLQuery) {
            requestPayload.metoroQLQuery = metoroQLQuery;
        } else {
            // Otherwise use the standard filters
            requestPayload.filters = filters;
            requestPayload.excludeFilters = excludeFilterToUse;
            requestPayload.regexes = regexes;
            requestPayload.excludeRegexes = excludeRegexes;
            requestPayload.transforms = transforms;
        }

        const d: AxiosPromise<LogsResponse> = axios.post("/api/v1/logs", requestPayload, {
                signal: abort.signal
            }
        )
        const awaited = (await d).data;
        let awaitedLogs = plainToInstance(LogsResponse, awaited)
        for (let log of awaitedLogs.logs) {
            // Convert the attributes to a Map
            // Check if map
            if (!(log.logAttributes instanceof Map)) {
                log.logAttributes = new Map(Object.entries(log.logAttributes));
            }
            if (!(log.resourceAttributes instanceof Map)) {
                // Convert resource attributes to a Map
                // This is needed because the backend returns an object
                log.resourceAttributes = new Map(Object.entries(log.resourceAttributes));
            }
        }
        setLogs(prevState => {
            if (prevMaxTime === undefined) {
                return {...prevState, logs: awaitedLogs.logs}
            } else {
                return {...prevState, logs: [...prevState.logs, ...awaitedLogs.logs]}
            }
        })
        setIsLogsLoading(false)
    } catch (e) {
        // @ts-ignore
        if (e.code === "ERR_CANCELED") {
            return;
        }
        console.error(e);
        setIsLogsLoading(false)

    }
}

async function updateLogSummary(setIsFilterAttributesLoading: (value: (((prevState: boolean) => boolean) | boolean)) => void, filter: Map<string, string[]>, excludeFilter: Map<string, string[]>, timeRange: TimeRange, regexes: string[], excludeRegexes: string[], environments: string[], setLogs: (value: (((prevState: Logs) => Logs) | Logs)) => void,
                                abortController: AbortController | undefined, setAbortController: (value: (((prevState: (AbortController | undefined)) => (AbortController | undefined)) | AbortController | undefined)) => void,
                                openFilters: Map<string, boolean>, transforms: Transform[],
) {
    try {
        abortController?.abort();
        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        const filters = Object.fromEntries(filter)
        const excludeFilters = Object.fromEntries(excludeFilter);
        const startEnd = timeRange.getStartEnd();
        for (let attribute of openFilters.keys()) {
            if (openFilters.get(attribute) !== true) {
                continue;
            }
            const request = {
                "startTime": Math.floor(startEnd[0].getTime() / 1000),
                "endTime": Math.floor(startEnd[1].getTime() / 1000),
                // Removed because we want to keep everything for now
                "excludeFilters": excludeFilters,
                // Removed because we want to keep everything for now
                "filters": filters,
                // Removed because we want to keep everything for now
                "regexes": regexes,
                // Removed because we want to keep everything for now
                "excludeRegexes": excludeRegexes,
                "environments": environments[0] === "" ? [] : environments,
                "attribute": attribute,
                "transforms": transforms,
            }

            if (attribute == "service.name") {
                (request as any)["limit"] = 1000
            }

            axios.post("/api/v1/logsSummaryIndividualAttribute", request,
                {signal: newAbortController.signal}
            ).then((response) => {
                let attributes = response.data.attribute;
                setLogs(prevState => {
                    let prevMap = prevState.filterAttributes;
                    prevMap.set(attribute, attributes);
                    return {...prevState, filterAttributes: prevMap}
                })
            }).catch(e => {
                // @ts-ignore
                if (e.code === "ERR_CANCELED") {
                    return;
                }
                console.error(e);
            })
        }
    } catch (e) {
        // @ts-ignore
        if (e.code === "ERR_CANCELED") {
            return;
        }
        console.error(e);
        setIsFilterAttributesLoading(false)
    }
}

const Logs = (props: LogsProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [logs, setLogs] = useState<Logs>({logs: [], filterAttributes: new Map()});
    const [prevMaxTime, setPrevMaxTime] = useState<number>();
    const timeRange = useSelector(timerange.selectors.getTimeRange)
    const [timelineViewerProps, setLogTimelineViewerProps] = useState<TimelineViewerProps | undefined>(undefined);
    const [isTimelineLoading, setIsTimelineLoading] = useState<boolean>(true);
    const [isLogsLoading, setIsLogsLoading] = useState<boolean>(true);
    const [isFilterAttributesLoading, setIsFilterAttributesLoading] = useState<boolean>(false);
    const [ascending, setAscending] = useState<boolean>(false);
    const [environments, setEnvironments] = useState<string[]>([]);
    const [customColumns, setCustomColumns] = useState<string[]>(() => {
        const customColumnsParam = searchParams.get("customColumns") || "";
        if (customColumnsParam !== "") {
            return JSON.parse(customColumnsParam);
        }
        return [];
    });
    const debouncedUpdateLogMetrics = useDebouncedCallback(updateLogMetrics, 10);
    const [updateLogMetricsAbortController, setUpdateLogMetricsAbortController] = useState<AbortController>(new AbortController());
    const debouncedUpdateLogs = useDebouncedCallback(updateLogs, 10);
    const [updateLogsAbortController, setUpdateLogsAbortController] = useState<AbortController | undefined>(undefined);
    const debouncedUpdateLogSummary = useDebouncedCallback(updateLogSummary, 10);
    const [logsSummaryAbortController, setLogsSummaryAbortController] = useState<AbortController | undefined>(undefined);

    // MetoroQL mode state
    const [isMetoroQLMode, setIsMetoroQLModeState] = useState<boolean>(() => {
        const metoroQLMode = searchParams.get("metoroQLMode");
        return metoroQLMode === "true";
    });
    const [metoroQLQuery, setMetoroQLQueryState] = useState<string>(() => {
        return searchParams.get("metoroQLQuery") || "";
    });
    const [isValidatingQuery, setIsValidatingQuery] = useState<boolean>(false);
    const [isQueryValid, setIsQueryValid] = useState<boolean>(true);
    const {toast} = useToast();

    // Create debounced versions of state and URL updates
    const debouncedSetMetoroQLQueryState = useDebouncedCallback(
        (query: string) => {
            setMetoroQLQueryState(query);
        },
        1000 // 1 second delay
    );

    const debouncedUpdateURL = useDebouncedCallback(
        (query: string) => {
            setSearchParams(prev => {
                let existing = new URLSearchParams(window.location.search);
                existing.set("metoroQLQuery", query);
                return existing;
            });
        },
        1000 // 1 second delay
    );

    // Listen for changes in URL parameters to update MetoroQL state
    useEffect(() => {
        const metoroQLMode = searchParams.get("metoroQLMode");
        const metoroQLQueryParam = searchParams.get("metoroQLQuery");

        // Update MetoroQL mode state if URL parameter changes
        setIsMetoroQLModeState(metoroQLMode === "true");

        // Update MetoroQL query state if URL parameter changes
        setMetoroQLQueryState(metoroQLQueryParam || "");
    }, [searchParams]);

    // Wrapper functions to update URL search parameters
    const setIsMetoroQLMode = (mode: boolean) => {
        setIsMetoroQLModeState(mode);
        setSearchParams(prev => {
            let existing = new URLSearchParams(window.location.search);
            existing.set("metoroQLMode", mode.toString());

            // Remove parameters that are no longer relevant after mode switch
            if (mode) {
                // When switching to MetoroQL mode, remove filter-related parameters
                existing.delete("filter");
                existing.delete("excludeFilter");
                existing.delete("regexes");
                existing.delete("excludeRegexes");
            } else {
                // When switching to standard mode, remove MetoroQL query parameter
                existing.delete("metoroQLQuery");
            }

            return existing;
        });
    };

    const setMetoroQLQuery = (query: string) => {
        // Use the debounced versions for state and URL updates
        debouncedSetMetoroQLQueryState(query);
        debouncedUpdateURL(query);
    };

    // Validate MetoroQL query when it changes
    useEffect(() => {
        if (!isMetoroQLMode || !metoroQLQuery) {
            setIsQueryValid(true);
            return;
        }

        const validateQuery = async () => {
            setIsValidatingQuery(true);
            try {
                const response = await axios.post("/api/v1/metoroql/logs/validate", {
                    query: metoroQLQuery
                });
                setIsQueryValid(response.data.valid);
                if (!response.data.valid) {
                    toast({
                        className: "bg-red-500/20 border border-red-500 text-textmedium rounded",
                        title: "Invalid MetoroQL query",
                        description: response.data.error || "The query is not valid for logs",
                    });
                }
            } catch (error) {
                console.error("Error validating MetoroQL query:", error);
                setIsQueryValid(false);
                toast({
                    className: "bg-red-500/20 border border-red-500 text-textmedium rounded",
                    title: "Error validating query",
                    description: "Failed to validate the MetoroQL query",
                });
            } finally {
                setIsValidatingQuery(false);
            }
        };

        // Debounce validation to avoid too many requests
        const timeoutId = setTimeout(validateQuery, 500);
        return () => clearTimeout(timeoutId);
    }, [metoroQLQuery, isMetoroQLMode, toast]);


    // OpenFilters is used to know what we need to know what values we need to pull when something updates
    const [openFilters, setOpenFilters] = useState<Map<string, boolean>>(() => {
        let o = new Map<string, boolean>();
        o.set("log_level", true)
        o.set("service.name", true)
        return o;
    });

    // Get all the log attributes to filter by
    useEffect(() => {
        axios.get("/api/v1/logsSummaryAttributes").then((response) => {
            let attributes = response.data.attributes;
            setLogs(prevState => {
                let newMap = new Map<string, Attribute[]>(prevState.filterAttributes);
                for (let attribute of attributes) {
                    if (!newMap.has(attribute)) {
                        newMap.set(attribute, [])
                    }
                }
                return {...prevState, filterAttributes: newMap}
            })
        })
    }, [])

    const [filter, setFilter] = useState<Map<string, string[]>>(() => {
        const filter = new Map<string, string[]>();
        const search = searchParams.get("filter");
        if (search !== null) {
            const filterObject = JSON.parse(search);
            for (const [key, value] of Object.entries(filterObject)) {
                filter.set(key, value as string[])
            }
        }
        if (props.service !== undefined) {
            filter.set("service.name", [props.service])
        }
        if (filter.get("ServiceName") !== undefined) {
            filter.delete("ServiceName")
        }
        return filter;
    });

    useEffect(() => {
        if (props.service !== undefined) {
            filter.set("service.name", [props.service])
        }
    }, [props.service]);

    const [transforms, setTransforms] = useState<Transform[]>(() => {
        const transformsParam = searchParams.get("transforms") || "";
        if (transformsParam !== "") {
            return JSON.parse(transformsParam) as Transform[];
        }
        return [];
    });

    useEffect(() => {
        const transformsParam = searchParams.get("transforms") || "";
        if (transformsParam !== "") {
            setTransforms(JSON.parse(transformsParam) as Transform[]);
        }
    }, [searchParams]);

    const [excludeFilter, setExcludeFilter] = useState<Map<string, string[]>>(
        () => {
            const excludeFilter = new Map<string, string[]>();
            const search = searchParams.get("excludeFilter");
            if (search !== null) {
                const excludeFilterObject = JSON.parse(search);
                for (const [key, value] of Object.entries(excludeFilterObject)) {
                    excludeFilter.set(key, value as string[])
                }
            }
            return excludeFilter;
        }
    );
    const [regexes, setRegexes] = useState<string[]>(
        () => {
            const regexes = searchParams.get("regexes") || "";
            if (regexes !== "") {
                return JSON.parse(regexes)
            }
            return []
        }
    );
    const [excludeRegexes, setExcludeRegexes] = useState<string[]>(
        () => {
            const excludeRegexes = searchParams.get("excludeRegexes") || "";
            if (excludeRegexes !== "") {
                return JSON.parse(excludeRegexes)
            }
            return []
        }
    );

    useEffect(() => {
        let filterJson = searchParams.get("regexes") || "";
        if (filterJson !== "") {
            setRegexes(JSON.parse(filterJson))
        }
        filterJson = searchParams.get("excludeRegexes") || "";
        if (filterJson !== "") {
            setExcludeRegexes(JSON.parse(filterJson))
        }
    }, [searchParams])

    useEffect(() => {
        let environments = searchParams.get("environment");
        if (environments !== null) {
            setEnvironments([environments])
        }
    }, [searchParams])

    useEffect(() => {
        let filterJson = searchParams.get("excludeFilter") || "";
        if (filterJson !== "") {
            const excludeFilterMap = new Map<string, string[]>();
            const excludeFilterObject = JSON.parse(filterJson);
            for (const [key, value] of Object.entries(excludeFilterObject)) {
                excludeFilterMap.set(key, value as string[])
            }
            setExcludeFilter(excludeFilterMap)
        }
        filterJson = searchParams.get("filter") || "";
        if (filterJson !== "") {
            const filterMap = new Map<string, string[]>();
            const filterObject = JSON.parse(filterJson);
            for (const [key, value] of Object.entries(filterObject)) {
                filterMap.set(key, value as string[])
            }
            setFilter(filterMap)
        }

    }, [searchParams])


    // Ascending query param
    useEffect(() => {
        const ascending = searchParams.get("ascending");
        if (ascending !== null) {
            setAscending(ascending === "true")
        }
    }, [searchParams])

    // CustomColumns query param
    useEffect(() => {
        const customColumnsParam = searchParams.get("customColumns");
        if (customColumnsParam !== null) {
            setCustomColumns(JSON.parse(customColumnsParam));
        }
    }, [searchParams])

    useEffect(() => {
        // Remove all of the filters, regexes and exclude regexes when from the search params when the service is removed
        return () => {
            if (window.location.pathname === "/service") {
                setSearchParams(() => {
                    let existing = new URLSearchParams(window.location.search)
                    existing.delete("filter")
                    existing.delete("excludeFilter")
                    existing.delete("regexes")
                    existing.delete("excludeRegexes")
                    existing.delete("transforms")
                    return existing
                })
            }
        }
    }, [])


    useEffect(() => {
        debouncedUpdateLogMetrics(
            props,
            environments,
            timeRange,
            filter,
            excludeFilter,
            regexes,
            excludeRegexes,
            setLogTimelineViewerProps,
            setIsTimelineLoading,
            updateLogMetricsAbortController,
            setUpdateLogMetricsAbortController,
            transforms,
            isMetoroQLMode,
            metoroQLQuery,
        );
    }, [filter, excludeFilter, regexes, excludeRegexes, timeRange, environments, isMetoroQLMode, metoroQLQuery, transforms]);


    // Reset prevMaxTime when filter changes so that we can fetch new logs
    useEffect(() => {
        setPrevMaxTime(undefined)
    }, [filter, excludeFilter, regexes, excludeFilter, environments]);


    const scrolledToBottomHandler = () => {
        if (logs.logs.length === 0) {
            return
        }
        let prevMaxTime = logs.logs[logs.logs.length - 1].time + 1;
        setPrevMaxTime(prevMaxTime)
    }

    // Let's reset the prevMaxTime when the ascending order changes so that we can fetch new logs
    useEffect(() => {
        setPrevMaxTime(undefined)
    }, [ascending])

    // Reset prevMaxTime when customColumns changes so that we can fetch new logs with the updated columns
    useEffect(() => {
        setPrevMaxTime(undefined)
    }, [customColumns])

    useEffect(() => {
        // Get logs
        debouncedUpdateLogs(
            setIsLogsLoading,
            filter,
            timeRange,
            prevMaxTime,
            excludeFilter,
            regexes,
            excludeRegexes,
            ascending,
            environments,
            setLogs,
            updateLogsAbortController,
            setUpdateLogsAbortController,
            transforms,
            isMetoroQLMode,
            metoroQLQuery,
        );
    }, [prevMaxTime, excludeFilter, filter, regexes, excludeRegexes, timeRange, ascending, environments, isMetoroQLMode, metoroQLQuery, transforms]);

    useEffect(() => {
        if (props.justLogsFeed) {
            // In the case where it's just the trace feed, we don't need the metrics
            return;
        }
        debouncedUpdateLogSummary(setIsFilterAttributesLoading, filter, excludeFilter, timeRange, regexes, excludeRegexes, environments, setLogs, logsSummaryAbortController, setLogsSummaryAbortController, openFilters, transforms);
    }, [filter, excludeFilter, regexes, excludeRegexes, timeRange, environments, openFilters, transforms]);

    if (props.justLogsFeed) {
        return <LogFeed
            filter={filter} setFilter={setFilter}
            excludeFilter={excludeFilter} setExcludeFilter={setExcludeFilter}
            ascending={ascending} setAscending={(ascending: boolean) => {
            setSearchParams(prev => {
                let existing = new URLSearchParams(window.location.search)
                existing.set("ascending", ascending.toString())
                return existing
            })
        }} removeService={props.removeService}
            logs={logs.logs}
            scrolledToBottomHandler={scrolledToBottomHandler}
            regexes={regexes}
            setRegexes={setRegexes}
            excludeRegexes={excludeRegexes}
            setExcludeRegexes={setExcludeRegexes}
            showExpand={true}
            expandOnClick={props.expandOnClick}
            isFilterShown={true}
            customColumns={customColumns}
            setCustomColumns={(columns: string[]) => {
                setCustomColumns(columns);
                setSearchParams(prev => {
                    let existing = new URLSearchParams(window.location.search);
                    existing.set("customColumns", JSON.stringify(columns));
                    return existing;
                });
            }}
        />
    }

    let logsInner = <div className={"flex flex-col grow shrink min-h-0 min-w-0"}>
        <div className={"flex flex-none relative"}>
            {isTimelineLoading && <LoadingSpinner className={`absolute top-1/2 left-1/2 z-40"}`}/>}
            <TimelineViewer {...timelineViewerProps!}/>
        </div>
        <MainLogsView
            dropDownFunction={
                (key: string) => {
                    let filters = Object.fromEntries(filter);
                    let excludeFilters = Object.fromEntries(excludeFilter);
                    const request = {
                        "startTime": Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
                        "endTime": Math.floor(timeRange.getStartEnd()[1].getTime() / 1000),
                        // Removed because we want to keep everything for now
                        "excludeFilters": excludeFilters,
                        // Removed because we want to keep everything for now
                        "filters": filters,
                        // Removed because we want to keep everything for now
                        "regexes": regexes,
                        // Removed because we want to keep everything for now
                        "excludeRegexes": excludeRegexes,
                        "environments": environments[0] === "" ? [] : environments,
                        "attribute": key,
                        "transforms": transforms,
                    }

                    if (key == "service.name") {
                        (request as any)["limit"] = 1000
                    }

                    return axios.post("/api/v1/logsSummaryIndividualAttribute", request).then((response) => {
                        return response.data.attribute;
                    })
                }
            }
            ascending={ascending}
            setIsOpened={setOpenFilters}
            setAscending={(ascending: boolean) => setSearchParams(prev => {
                let existing = new URLSearchParams(window.location.search)
                existing.set("ascending", ascending.toString())
                return existing
            })}
            isLogsLoading={isLogsLoading} isFilterAttributesLoading={isFilterAttributesLoading}
            setFreeTextFilter={(filter: string) => setSearchParams(prev => {
                    let existing = new URLSearchParams(window.location.search)
                    existing.set("search", filter)
                    return existing
                }
            )}
            setFilter={(filter: Map<string, string[]>) => setSearchParams(prev => {
                let existing = new URLSearchParams(window.location.search)
                existing.set("filter", JSON.stringify(Object.fromEntries(filter)))
                return existing
            })}
            filter={filter}
            setExcludeFilter={(filter: Map<string, string[]>) => setSearchParams(prev => {
                let existing = new URLSearchParams(window.location.search)
                existing.set("excludeFilter", JSON.stringify(Object.fromEntries(filter)))
                return existing
            })}
            excludeFilter={excludeFilter}
            regexes={regexes}
            setRegexes={(regexes: string[]) => setSearchParams(prev => {
                let existing = new URLSearchParams(window.location.search)
                existing.set("regexes", JSON.stringify(regexes))
                return existing
            })}
            excludeRegexes={excludeRegexes}
            setExcludeRegexes={(regexes: string[]) => setSearchParams(prev => {
                let existing = new URLSearchParams(window.location.search)
                existing.set("excludeRegexes", JSON.stringify(regexes))
                return existing
            })}
            logs={logs}
            scrolledToBottomHandler={scrolledToBottomHandler}
            isMetoroQLMode={isMetoroQLMode}
            setIsMetoroQLMode={setIsMetoroQLMode}
            metoroQLQuery={metoroQLQuery}
            setMetoroQLQuery={setMetoroQLQuery}
            customColumns={customColumns}
            setCustomColumns={(columns: string[]) => {
                setCustomColumns(columns);
                setSearchParams(prev => {
                    let existing = new URLSearchParams(window.location.search);
                    existing.set("customColumns", JSON.stringify(columns));
                    return existing;
                });
            }}
            transforms={transforms}
            setTransforms={(newTransformsOrUpdater: SetStateAction<Transform[]>) => {
                // Handle both direct updates and functional updates
                const newTransforms = typeof newTransformsOrUpdater === 'function' 
                    ? newTransformsOrUpdater(transforms) 
                    : newTransformsOrUpdater;

                setSearchParams(prev => {
                    let existing = new URLSearchParams(window.location.search);
                    existing.set("transforms", JSON.stringify(newTransforms));
                    return existing;
                });
            }}
        />
    </div>;
    if (props.noBaseView) {
        return logsInner;
    }
    return (
        <BaseView title={"Logs"}>
            <div className={"p-4 flex flex-col grow shrink bg-backgrounddark min-w-0 min-h-0"}>
                {logsInner}
            </div>
        </BaseView>
    )
}

interface LogsComponentProps {
    smallerTitleEnabled?: boolean
    justLogsFeed?: boolean
    removeService?: boolean
    expandOnClick?: () => void
    noBaseView?: boolean
    filter: Map<string, string[]>
    setFilter: (filter: Map<string, string[]>) => void
    excludeFilter: Map<string, string[]>
    setExcludeFilter: (filter: Map<string, string[]>) => void
    regexes: string[]
    setRegexes: (regexes: string[]) => void
    excludeRegexes: string[]
    setExcludeRegexes: (regexes: string[]) => void
    ascending: boolean
    setAscending: (ascending: boolean) => void
    setFreeTextFilter: (filter: string) => void
    hideSearchBar?: boolean
    dialogRef?: React.RefObject<HTMLElement>
    showHistogram?: boolean,
    isMetoroQLMode?: boolean
    setIsMetoroQLMode?: (isMetoroQLMode: boolean) => void
    metoroQLQuery?: string
    setMetoroQLQuery?: (query: string) => void
    transforms: Transform[]
    setTransforms: Dispatch<SetStateAction<Transform[]>>
    customColumns: string[]
    setCustomColumns: (columns: string[]) => void
}

export const LogsComponent = (props: LogsComponentProps) => {
    const [logs, setLogs] = useState<Logs>({logs: [], filterAttributes: new Map()});
    const [prevMaxTime, setPrevMaxTime] = useState<number>();
    const timeRange = useSelector(timerange.selectors.getTimeRange)
    const [timelineViewerProps, setLogTimelineViewerProps] = useState<TimelineViewerProps | undefined>(undefined);
    const [isTimelineLoading, setIsTimelineLoading] = useState<boolean>(true);
    const [isLogsLoading, setIsLogsLoading] = useState<boolean>(true);
    const [isFilterAttributesLoading, setIsFilterAttributesLoading] = useState<boolean>(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [environments, setEnvironments] = useState<string[]>([]);
    const debouncedUpdateLogMetrics = useDebouncedCallback(updateLogMetrics, 10);
    const [updateLogMetricsAbortController, setUpdateLogMetricsAbortController] = useState<AbortController>(new AbortController());
    const debouncedUpdateLogs = useDebouncedCallback(updateLogs, 10);
    const [updateLogsAbortController, setUpdateLogsAbortController] = useState<AbortController | undefined>(undefined);
    const debouncedUpdateLogSummary = useDebouncedCallback(updateLogSummary, 10);
    const [logsSummaryAbortController, setLogsSummaryAbortController] = useState<AbortController | undefined>(undefined);

    // OpenFilters is used to know what we need to know what values we need to pull when something updates
    const [openFilters, setOpenFilters] = useState<Map<string, boolean>>(() => {
        let o = new Map<string, boolean>();
        o.set("log_level", true)
        o.set("service.name", true)
        return o;
    });

    // Get all the log attributes to filter by
    useEffect(() => {
        axios.get("/api/v1/logsSummaryAttributes").then((response) => {
            let attributes = response.data.attributes;
            setLogs(prevState => {
                let newMap = new Map<string, Attribute[]>(prevState.filterAttributes);
                for (let attribute of attributes) {
                    if (!newMap.has(attribute)) {
                        newMap.set(attribute, [])
                    }
                }
                return {...prevState, filterAttributes: newMap}
            })
        }).catch((e) => {
            if (e.code === "ERR_CANCELED") {
                return;
            } else {
                console.error(e);
            }
        })
    }, [])

    useEffect(() => {
        let environments = searchParams.get("environment");
        if (environments !== null) {
            setEnvironments([environments])
        }
    }, [searchParams])

    useEffect(() => {
        if (props.showHistogram) {
            debouncedUpdateLogMetrics(
                props,
                environments,
                timeRange,
                props.filter,
                props.excludeFilter,
                props.regexes,
                props.excludeRegexes,
                setLogTimelineViewerProps,
                setIsTimelineLoading,
                updateLogMetricsAbortController,
                setUpdateLogMetricsAbortController,
                props.transforms,
                props.isMetoroQLMode,
                props.metoroQLQuery,
            );
        }
    }, [props.filter, props.excludeFilter, props.regexes, props.excludeRegexes, timeRange, environments, props.isMetoroQLMode, props.metoroQLQuery, props.transforms]);


    // Reset prevMaxTime when filter changes so that we can fetch new logs
    useEffect(() => {
        setPrevMaxTime(undefined)
    }, [props.filter, props.excludeFilter, props.regexes, props.excludeFilter, environments, props.transforms]);


    const scrolledToBottomHandler = () => {
        if (logs.logs.length === 0) {
            return
        }
        let prevMaxTime = logs.logs[logs.logs.length - 1].time + 1;
        setPrevMaxTime(prevMaxTime)
    }

    // Let's reset the prevMaxTime when the ascending order changes so that we can fetch new logs
    useEffect(() => {
        setPrevMaxTime(undefined)
    }, [props.ascending])

    // Reset prevMaxTime when customColumns changes so that we can fetch new logs with the updated columns
    useEffect(() => {
        setPrevMaxTime(undefined)
    }, [props.customColumns])

    useEffect(() => {
        // Get logs
        debouncedUpdateLogs(setIsLogsLoading, props.filter, timeRange, prevMaxTime, props.excludeFilter, props.regexes, props.excludeRegexes, props.ascending, environments, setLogs, updateLogsAbortController, setUpdateLogsAbortController, props.transforms, props.isMetoroQLMode, props.metoroQLQuery);
    }, [prevMaxTime, props.excludeFilter, props.filter, props.regexes, props.excludeRegexes, timeRange, props.ascending, environments, props.isMetoroQLMode, props.metoroQLQuery, props.transforms]);

    useEffect(() => {
        if (props.justLogsFeed) {
            // In the case where it's just the trace feed, we don't need the metrics
            return;
        }
        debouncedUpdateLogSummary(setIsFilterAttributesLoading, props.filter, props.excludeFilter, timeRange, props.regexes, props.excludeRegexes, environments, setLogs, logsSummaryAbortController, setLogsSummaryAbortController, openFilters, props.transforms);
    }, [props.filter, props.excludeFilter, props.regexes, props.excludeRegexes, timeRange, environments, openFilters, props.transforms]);

    return <div className={"flex flex-col grow shrink min-h-0 min-w-0"}>
        {props.showHistogram && <div className={"flex flex-none relative"}>
            {isTimelineLoading && <LoadingSpinner className={`absolute top-1/2 left-1/2 z-40"}`}/>}
            <TimelineViewer {...timelineViewerProps!}/>
        </div>}
        <MainLogsView
            dropDownFunction={
                (key: string) => {
                    let filters = Object.fromEntries(props.filter);
                    let excludeFilters = Object.fromEntries(props.excludeFilter);
                    const request = {
                        "startTime": Math.floor(timeRange.getStartEnd()[0].getTime() / 1000) - 3600,
                        "endTime": Math.floor(timeRange.getStartEnd()[1].getTime() / 1000),
                        // Removed because we want to keep everything for now
                        "excludeFilters": excludeFilters,
                        // Removed because we want to keep everything for now
                        "filters": filters,
                        // Removed because we want to keep everything for now
                        "regexes": props.regexes,
                        // Removed because we want to keep everything for now
                        "excludeRegexes": props.excludeRegexes,
                        "environments": environments[0] === "" ? [] : environments,
                        "attribute": key,
                        "transforms": props.transforms,
                    }

                    if (key == "service.name") {
                        // @ts-ignore
                        request["limit"] = 1000
                    }

                    return axios.post("/api/v1/logsSummaryIndividualAttribute", request).then((response) => {
                        return response.data.attribute;
                    })
                }
            }
            ascending={props.ascending}
            setIsOpened={setOpenFilters}
            setAscending={props.setAscending}
            isLogsLoading={isLogsLoading} isFilterAttributesLoading={isFilterAttributesLoading}
            setFreeTextFilter={props.setFreeTextFilter}
            setFilter={props.setFilter}
            filter={props.filter}
            setExcludeFilter={props.setExcludeFilter}
            excludeFilter={props.excludeFilter}
            regexes={props.regexes}
            setRegexes={props.setRegexes}
            excludeRegexes={props.excludeRegexes}
            setExcludeRegexes={props.setExcludeRegexes}
            logs={logs}
            scrolledToBottomHandler={scrolledToBottomHandler}
            hideSearchBar={props.hideSearchBar}
            dialogRef={props.dialogRef}
            isMetoroQLMode={props.isMetoroQLMode}
            setIsMetoroQLMode={props.setIsMetoroQLMode}
            metoroQLQuery={props.metoroQLQuery}
            setMetoroQLQuery={props.setMetoroQLQuery}
            customColumns={props.customColumns}
            setCustomColumns={props.setCustomColumns}
            transforms={props.transforms}
            setTransforms={props.setTransforms}
        />
    </div>;
}

export default Logs;
